"use client";

import {
	useState,
	useRef,
	useEffect,
	useMemo,
	useDeferredValue,
	useId,
	type FormEvent,
	type KeyboardEvent,
} from "react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { SearchIcon, ArrowRight, Clock, Loader2, Sparkles, X } from "lucide-react";
import { useSearchIndex } from "@/lib/search/use-search-index";
import { fuseSearchWithDidYouMean } from "@/lib/search/fuse-search";
import type { SearchProduct } from "@/lib/search";
import { useRecentSearches } from "@/lib/use-recent-searches";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import { defaultLocale, formatPrice, isValidLocale } from "@/config/locale";
import { createSearchSubmissionId } from "@/lib/search/submission-id";
import { rememberInternalSource } from "@/lib/analytics/internal-source";

const MIN_QUERY_LENGTH = 2;

interface SearchInputWithSuggestionsProps {
	channel: string;
	locale: string;
	placeholder?: string;
	variant?: "modal" | "pill";
}

export function SearchInputWithSuggestions({
	channel,
	locale,
	placeholder,
	variant = "modal",
}: SearchInputWithSuggestionsProps) {
	const router = useRouter();
	const listboxId = useId();
	const tSearch = useTranslations("search");
	const resolvedPlaceholder = placeholder ?? tSearch("searchPlaceholder");
	const [query, setQuery] = useState("");
	// Keep the input responsive: typing updates `query` immediately, but the
	// per-keystroke Fuse search (includeMatches + ~20 keys) runs on the deferred
	// value so it never blocks the keystroke → lower INP on the header search.
	const deferredQuery = useDeferredValue(query);
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const submissionIdRef = useRef<HTMLInputElement>(null);
	const { fuse, products, ensureLoaded, isLoading } = useSearchIndex(channel);
	const {
		searches: recentSearches,
		add: addRecentSearch,
		clear: clearRecentSearches,
		mounted,
	} = useRecentSearches();

	// Deferred search — keystrokes stay responsive, Fuse runs on the deferred value.
	const { suggestions, didYouMean } = useMemo(() => {
		if (!fuse || !deferredQuery.trim() || deferredQuery.trim().length < MIN_QUERY_LENGTH) {
			return { suggestions: [] as SearchProduct[], didYouMean: undefined };
		}
		const result = fuseSearchWithDidYouMean(fuse, deferredQuery, locale, 6, products);
		return { suggestions: result.products, didYouMean: result.didYouMean };
	}, [fuse, products, deferredQuery, locale]);

	// Reference-currency labels for the visible suggestions (same policy as cart/PLP).
	// `priceResolveItems` is memoized on `suggestions` (itself memoized per deferred query)
	// so the hook sees a stable items identity per query; the hook additionally
	// content-stabilizes items internally and refetches on geo-pref change.
	const priceResolveItems = useMemo(
		() => suggestions.map((p) => ({ id: p.id, amount: p.price, currency: p.currency })),
		[suggestions],
	);
	const presMap = usePricingResolvePresentations(locale, channel, priceResolveItems);
	const displayLocale = isValidLocale(locale) ? locale : defaultLocale;
	const suggestionPriceLabel = (product: SearchProduct) =>
		presMap.get(product.id)?.primaryLabel ?? formatPrice(product.price, product.currency, displayLocale);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		function handleVisibilityChange() {
			if (
				document.visibilityState === "visible" &&
				(isOpen || containerRef.current?.contains(document.activeElement))
			) {
				ensureLoaded();
			}
		}

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [ensureLoaded, isOpen]);

	const handleSuggestionClick = (product: SearchProduct, position: number) => {
		const searchSubmissionId = createSearchSubmissionId();
		posthog.capture("search_submitted", {
			query: query.trim(),
			search_submission_id: searchSubmissionId,
			source: "header_suggestion",
			channel,
			locale,
		});
		posthog.capture("search_suggestion_clicked", {
			query: query.trim(),
			search_submission_id: searchSubmissionId,
			product_slug: product.slug,
			position,
			position_schema_version: 2,
			channel,
			locale,
		});
		posthog.capture("search_result_clicked", {
			query: query.trim(),
			search_submission_id: searchSubmissionId,
			product_slug: product.slug,
			position,
			position_schema_version: 2,
			surface: "suggestion",
			match_strategy: product.matchStrategy,
			is_available: product.isAvailable,
			channel,
			locale,
		});
		setIsOpen(false);
		setActiveIndex(-1);
		addRecentSearch(query);
		setQuery("");
		// Same attribution as a results-page card: the PDP visit (and the add that may
		// follow) belongs to this search, handed over in the tab so the URL stays clean.
		// Detail is the submission id, never the query.
		const href = `/${locale}/${channel}/products/${product.slug}`;
		rememberInternalSource(href, "search", searchSubmissionId);
		router.push(href);
	};

	const handleDidYouMeanClick = () => {
		if (didYouMean) {
			posthog.capture("search_did_you_mean_clicked", {
				query: query.trim(),
				suggestion: didYouMean,
				surface: "suggestion",
				channel,
				locale,
			});
			setQuery(didYouMean);
			setActiveIndex(-1);
		}
	};

	const handleRecentSearchClick = (search: string) => {
		const searchSubmissionId = createSearchSubmissionId();
		posthog.capture("search_recent_clicked", {
			query: search,
			search_submission_id: searchSubmissionId,
			channel,
			locale,
		});
		posthog.capture("search_submitted", {
			query: search,
			search_submission_id: searchSubmissionId,
			source: "recent_search",
			channel,
			locale,
		});
		setQuery(search);
		setIsOpen(false);
		setActiveIndex(-1);
		router.push(`/${locale}/${channel}/search?query=${encodeURIComponent(search)}&sid=${searchSubmissionId}`);
	};

	const handleSubmit = (_e: FormEvent<HTMLFormElement>) => {
		const q = query.trim();
		if (q) {
			const searchSubmissionId = createSearchSubmissionId();
			if (submissionIdRef.current) submissionIdRef.current.value = searchSubmissionId;
			addRecentSearch(q);
			posthog.capture("search_submitted", {
				query: q,
				search_submission_id: searchSubmissionId,
				source: "header",
				channel,
				locale,
			});
		}
	};

	const featuredSuggestion = suggestions[0];
	const compactSuggestions = featuredSuggestion ? suggestions.slice(1) : suggestions;
	const showRecentSearches = mounted && !query.trim() && recentSearches.length > 0 && isOpen;
	const showSuggestions = isOpen && (suggestions.length > 0 || !!didYouMean || (isLoading && !fuse));
	const showDropdown = showRecentSearches || showSuggestions;
	const keyboardItems = showRecentSearches
		? recentSearches.map((value) => ({ kind: "recent" as const, value }))
		: suggestions.length > 0
			? suggestions.map((product) => ({ kind: "product" as const, product }))
			: didYouMean
				? [{ kind: "did_you_mean" as const, value: didYouMean }]
				: [];

	const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Escape") {
			setIsOpen(false);
			setActiveIndex(-1);
			return;
		}

		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			if (keyboardItems.length === 0) return;
			event.preventDefault();
			setIsOpen(true);
			setActiveIndex((current) => {
				if (event.key === "ArrowDown") return current >= keyboardItems.length - 1 ? 0 : current + 1;
				return current <= 0 ? keyboardItems.length - 1 : current - 1;
			});
			return;
		}

		if (event.key !== "Enter" || !isOpen || activeIndex < 0) return;
		const item = keyboardItems[activeIndex];
		if (!item) return;
		event.preventDefault();
		if (item.kind === "product") handleSuggestionClick(item.product, activeIndex + 1);
		if (item.kind === "recent") handleRecentSearchClick(item.value);
		if (item.kind === "did_you_mean") handleDidYouMeanClick();
	};

	return (
		<div ref={containerRef} className="relative w-full" onPointerEnter={ensureLoaded}>
			<form action={`/${locale}/${channel}/search`} onSubmit={handleSubmit}>
				<input ref={submissionIdRef} type="hidden" name="sid" />
				<label className="relative block">
					<span className="sr-only">{resolvedPlaceholder}</span>
					{variant === "modal" && (
						<span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
							<SearchIcon
								className="h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-foreground"
								aria-hidden
							/>
						</span>
					)}
					<input
						type="text"
						name="query"
						role="combobox"
						aria-autocomplete="list"
						aria-expanded={showDropdown}
						aria-controls={showDropdown ? listboxId : undefined}
						aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setActiveIndex(-1);
							if (e.target.value.trim().length >= MIN_QUERY_LENGTH) {
								ensureLoaded();
								setIsOpen(true);
							}
						}}
						onKeyDown={handleInputKeyDown}
						onFocus={() => {
							ensureLoaded();
							if (suggestions.length > 0 || didYouMean || isLoading) setIsOpen(true);
							else if (!query.trim() && recentSearches.length > 0) setIsOpen(true);
						}}
						placeholder={resolvedPlaceholder}
						autoComplete="off"
						minLength={MIN_QUERY_LENGTH}
						required
						className={
							variant === "pill"
								? "min-w-0 flex-1 border-0 bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
								: "hover:bg-secondary/80 focus:outline-hidden focus:border-primary/40 focus:ring-primary/20 h-10 w-full rounded-lg border border-transparent bg-secondary py-2 pl-11 pr-4 text-sm text-foreground transition-all placeholder:text-muted-foreground hover:border-border focus:bg-background focus:ring-2"
						}
					/>
				</label>
			</form>

			{showDropdown && (
				<div
					id={listboxId}
					role="listbox"
					className="search-suggestions-surface border-border/80 absolute left-0 right-0 top-full z-50 mt-2 max-h-[32rem] animate-dropdown-in overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-2xl"
				>
					{showRecentSearches && (
						<div className="border-border/80 border-b py-2">
							<div className="flex items-center justify-between px-4 pb-2">
								<span className="text-xs font-medium text-muted-foreground">{tSearch("recentSearches")}</span>
								<button
									type="button"
									onClick={clearRecentSearches}
									className="text-xs text-muted-foreground transition-colors hover:text-foreground"
								>
									<X className="h-3 w-3" />
								</button>
							</div>
							<ul>
								{recentSearches.map((search, index) => (
									<li key={search}>
										<button
											id={`${listboxId}-option-${index}`}
											role="option"
											aria-selected={activeIndex === index}
											type="button"
											onClick={() => handleRecentSearchClick(search)}
											className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent"
										>
											<Clock className="h-3.5 w-3.5 text-muted-foreground" />
											<span>{search}</span>
										</button>
									</li>
								))}
							</ul>
						</div>
					)}

					{isLoading && !fuse && (
						<div className="flex items-center gap-2.5 px-4 py-4 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" />
							<span>{tSearch("suggestionsLoading")}</span>
						</div>
					)}

					{didYouMean && suggestions.length === 0 && !isLoading && (
						<button
							id={`${listboxId}-option-0`}
							role="option"
							aria-selected={activeIndex === 0}
							type="button"
							onClick={handleDidYouMeanClick}
							className="border-border/80 flex w-full items-center gap-2 border-b px-4 py-3 text-left text-sm transition-colors hover:bg-accent"
						>
							<Sparkles className="h-3.5 w-3.5 text-primary" />
							<span>{tSearch("didYouMean", { suggestion: didYouMean })}</span>
						</button>
					)}

					{suggestions.length > 0 && (
						<div className="max-h-[28rem] overflow-auto">
							{featuredSuggestion && (
								<button
									id={`${listboxId}-option-0`}
									role="option"
									aria-selected={activeIndex === 0}
									type="button"
									onClick={() => handleSuggestionClick(featuredSuggestion, 1)}
									className="border-border/80 group block w-full border-b p-4 text-left transition-colors hover:bg-accent"
								>
									<div className="flex items-center gap-4">
										<div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
											{featuredSuggestion.thumbnailUrl ? (
												<Image
													src={featuredSuggestion.thumbnailUrl}
													alt={featuredSuggestion.thumbnailAlt || featuredSuggestion.name}
													fill
													className="object-cover transition-transform duration-500 group-hover:scale-105"
													sizes="80px"
												/>
											) : (
												<div className="flex h-full items-center justify-center text-muted-foreground">
													<SearchIcon className="h-5 w-5" />
												</div>
											)}
										</div>
										<div className="min-w-0 flex-1">
											<div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
												<Sparkles className="h-3.5 w-3.5 text-primary" />
												<span className="truncate">{featuredSuggestion.categoryName}</span>
											</div>
											<p className="mt-1 line-clamp-2 text-sm font-semibold text-foreground">
												<HighlightedText product={featuredSuggestion} />
											</p>
											<p className="mt-2 text-sm font-medium text-foreground">
												{suggestionPriceLabel(featuredSuggestion)}
											</p>
										</div>
									</div>
								</button>
							)}

							<ul className="py-2" role="presentation">
								{compactSuggestions.map((product, index) => (
									<li
										key={product.id}
										role="presentation"
										className="search-suggestion-result animate-fade-in-up opacity-0"
										style={{ animationDelay: `calc(min(${index}, 7) * var(--duration-stagger))` }}
									>
										<button
											id={`${listboxId}-option-${index + 1}`}
											role="option"
											aria-selected={activeIndex === index + 1}
											type="button"
											onClick={() => handleSuggestionClick(product, index + 2)}
											className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
										>
											<div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-muted">
												{product.thumbnailUrl ? (
													<Image
														src={product.thumbnailUrl}
														alt={product.thumbnailAlt || product.name}
														fill
														className="object-cover"
														sizes="48px"
													/>
												) : (
													<div className="flex h-full items-center justify-center text-muted-foreground">
														<SearchIcon className="h-4 w-4" />
													</div>
												)}
											</div>
											<div className="min-w-0 flex-1">
												<p className="truncate font-medium text-foreground">
													<HighlightedText product={product} />
												</p>
												{product.categoryName && (
													<p className="truncate text-xs text-muted-foreground">{product.categoryName}</p>
												)}
											</div>
											<span className="shrink-0 text-xs font-medium text-muted-foreground">
												{suggestionPriceLabel(product)}
											</span>
										</button>
									</li>
								))}
							</ul>
						</div>
					)}

					{suggestions.length > 0 && (
						<ul>
							<li className="border-border/80 border-t bg-muted">
								<Link
									href={`/${locale}/${channel}/search?query=${encodeURIComponent(query)}`}
									onClick={(event) => {
										const q = query.trim();
										if (q) {
											event.preventDefault();
											const searchSubmissionId = createSearchSubmissionId();
											posthog.capture("search_submitted", {
												query: q,
												search_submission_id: searchSubmissionId,
												source: "header",
												channel,
												locale,
												via: "view_all_results",
											});
											addRecentSearch(q);
											router.push(
												`/${locale}/${channel}/search?query=${encodeURIComponent(q)}&sid=${searchSubmissionId}`,
											);
										}
										setIsOpen(false);
										setActiveIndex(-1);
									}}
									className="flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium text-primary transition-colors hover:bg-accent hover:text-accent-foreground"
								>
									{tSearch("viewAllResults", { query })}
									<ArrowRight className="h-3.5 w-3.5" />
								</Link>
							</li>
						</ul>
					)}
				</div>
			)}
		</div>
	);
}

function HighlightedText({ product }: { product: SearchProduct }) {
	if (!product.highlights?.name) {
		return product.name;
	}

	return (
		<span
			className="[&_mark]:bg-primary/15 [&_mark]:rounded-sm [&_mark]:px-0.5 [&_mark]:text-foreground"
			dangerouslySetInnerHTML={{ __html: product.highlights.name }}
		/>
	);
}
